(() => {
  if (customElements.get('mbg-product-compatibility')) return;

  const sources = new Map();
  const controls = /[\u0000-\u001f\u007f-\u009f]/;
  const canonicalTag = (value, prefix) => typeof value === 'string' && value.length <= 255 && new RegExp(`^${prefix}-[a-z0-9]+(?:-[a-z0-9]+)*$`).test(value);
  const cleanText = (value, limit) => typeof value === 'string' && !controls.test(value) ? [...value.trim()].slice(0, limit).join('') : '';

  function safeUrl(value, base) {
    if (typeof value !== 'string' || value.length > 4096 || controls.test(value) || value.includes('\\')) return null;
    try {
      const rawPath = decodeURIComponent(value.split(/[?#]/, 1)[0]);
      if (controls.test(rawPath) || rawPath.includes('\\') || /(?:^|\/)\.{1,2}(?:\/|$)/.test(rawPath)) return null;
      const url = new URL(value, base);
      if (!/^https?:$/.test(url.protocol) || url.username || url.password) return null;
      return url;
    } catch {
      return null;
    }
  }

  function collectionUrl(value, preview) {
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
    const url = safeUrl(value, window.location.origin);
    if (!url || url.origin !== window.location.origin) return null;
    const match = url.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/([^/]+)\/?$/i);
    try {
      const handle = match ? decodeURIComponent(match[1]) : '';
      if ([...handle].length > 255 || !/^[\p{L}\p{M}\p{N}-]+$/u.test(handle)) return null;
    } catch {
      return null;
    }
    url.search = '';
    url.hash = '';
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  function resultUrl(value, base, vehicle, category, preview) {
    const url = safeUrl(value, base);
    if (!url || url.origin !== window.location.origin || url.searchParams.get('view') !== 'vehicle-products') return '';
    const match = url.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/all\/([^/]+)\/?$/i);
    try {
      const tags = match ? decodeURIComponent(match[1]).split('+') : [];
      if (tags.length !== 2 || !tags.includes(vehicle) || !tags.includes(category)) return '';
    } catch {
      return '';
    }
    url.hash = '';
    url.searchParams.delete('mbg_open_single');
    url.searchParams.delete('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  }

  function imageUrl(value, base) {
    const url = safeUrl(value, base);
    if (!url || url.protocol !== 'https:') return '';
    if (url.origin !== window.location.origin && (url.hostname !== 'cdn.shopify.com' || url.port)) return '';
    return url.href;
  }

  function readCards(html, source, preview) {
    const fragment = document.createElement('template');
    fragment.innerHTML = html;
    const navigation = fragment.content.querySelector('.mbg-collection-navigation');
    if (!navigation) return null;
    const cards = [];
    navigation.querySelectorAll('a.mbg-collection-navigation__card[data-mbg-vehicle-tag][data-mbg-category-tag]').forEach(card => {
      const vehicle = card.dataset.mbgVehicleTag;
      const category = card.dataset.mbgCategoryTag;
      if (!canonicalTag(vehicle, 'vehicle') || !canonicalTag(category, 'category')) return;
      const title = cleanText(card.querySelector('.mbg-collection-navigation__title')?.textContent, 160);
      const years = cleanText(card.querySelector('.mbg-collection-navigation__years')?.textContent, 80);
      const href = resultUrl(card.getAttribute('href'), source, vehicle, category, preview);
      const image = imageUrl(card.querySelector('.mbg-collection-navigation__image')?.getAttribute('src'), source);
      if (title && href && image) cards.push({ vehicle, category, title, years, href, image });
    });
    return cards;
  }

  async function requestHtml(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) return null;
      if (response.url) {
        const final = new URL(response.url);
        if (final.origin !== url.origin || final.pathname.replace(/\/$/, '') !== url.pathname.replace(/\/$/, '')) return null;
      }
      return await response.text();
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  function loadSource(source, sectionId, preview) {
    const key = source.href + '|' + sectionId;
    if (!sources.has(key)) {
      sources.set(key, (async () => {
        if (sectionId) {
          const sectionUrl = new URL(source.href);
          sectionUrl.searchParams.set('section_id', sectionId);
          const html = await requestHtml(sectionUrl);
          if (html !== null) {
            const cards = readCards(html, source.href, preview);
            if (cards !== null) return cards;
          }
        }
        const html = await requestHtml(source);
        return html === null ? [] : (readCards(html, source.href, preview) || []);
      })().catch(() => []));
    }
    return sources.get(key);
  }

  class MbgProductCompatibility extends HTMLElement {
    static get observedAttributes() { return ['data-source-section']; }

    connectedCallback() {
      this.configObserver?.disconnect();
      const config = this.querySelector('[data-mbg-compatibility-config]');
      if (config) {
        this.configObserver = new MutationObserver(() => this.refresh());
        this.configObserver.observe(config, { childList: true, characterData: true, subtree: true });
      }
      this.refresh();
    }

    disconnectedCallback() {
      this.configObserver?.disconnect();
      this.revision = (this.revision || 0) + 1;
      this.hidden = true;
    }

    attributeChangedCallback() {
      if (this.isConnected) this.refresh();
    }

    async refresh() {
      const revision = this.revision = (this.revision || 0) + 1;
      const list = this.querySelector('[data-mbg-compatibility-list]');
      this.hidden = true;
      list?.replaceChildren();
      try {
        const config = JSON.parse(this.querySelector('[data-mbg-compatibility-config]')?.textContent || 'null');
        const template = this.querySelector('template[data-mbg-compatibility-item]');
        if (!list || !template || !config || !Array.isArray(config.tags) || !Array.isArray(config.collections)) return;
        const tags = new Set(config.tags.filter(tag => typeof tag === 'string'));
        if (![...tags].some(tag => canonicalTag(tag, 'vehicle')) || ![...tags].some(tag => canonicalTag(tag, 'category'))) return;
        const current = new URL(window.location.href);
        const currentPreview = current.searchParams.get('preview_theme_id');
        const configuredPreview = String(config.previewThemeId || '');
        const preview = /^\d{1,20}$/.test(currentPreview || '') ? currentPreview : (/^\d{1,20}$/.test(configuredPreview) ? configuredPreview : '');
        const configuredSection = this.dataset.sourceSection || '';
        const sectionId = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,180}$/.test(configuredSection) ? configuredSection : '';
        const uniqueSources = new Map();
        for (const value of config.collections.slice(0, 8)) {
          const source = collectionUrl(value, preview);
          if (source) uniqueSources.set(source.href, source);
        }
        const groups = await Promise.all([...uniqueSources.values()].map(source => loadSource(source, sectionId, preview)));
        if (!this.isConnected || revision !== this.revision) return;
        const matches = new Map();
        groups.flat().forEach(card => {
          const key = card.vehicle + '|' + card.category;
          if (tags.has(card.vehicle) && tags.has(card.category) && !matches.has(key)) matches.set(key, card);
        });
        const output = document.createDocumentFragment();
        for (const card of matches.values()) {
          const item = template.content.cloneNode(true);
          const anchor = item.querySelector('a.mbg-product-compatibility__item');
          const image = item.querySelector('img');
          const name = item.querySelector('[data-mbg-compatibility-name]');
          const years = item.querySelector('[data-mbg-compatibility-years]');
          if (!anchor || !image || !name || !years) continue;
          anchor.href = card.href;
          image.src = card.image;
          name.textContent = card.title;
          years.textContent = card.years;
          years.hidden = !card.years;
          output.append(item);
        }
        list.replaceChildren(output);
        this.hidden = !list.childElementCount;
      } catch {
        if (revision === this.revision) this.hidden = true;
      }
    }
  }

  customElements.define('mbg-product-compatibility', MbgProductCompatibility);
})();
